import React, { useState, useEffect } from 'react';
import Network from '../../../models/Network.js';
import { useNavigate } from 'react-router-dom';

import DualColListElement from '../list_elements/DualColListElement.js';
import './Module.css';
import TypeTag from '../tags/TypeTag.js';
import MedicTag from '../tags/MedicTag.js';
import Header from '../Header.js';
import ReportTimeline from '../graphics/ReportTimeline.js';
import { primaryTypeTagNames, secondaryTypeTagNames, tertiaryTypeTagNames, auxiliaryTypeTagNames, otherTagNames } from '../../../models/tag_representations';

const key = process.env.REACT_APP_CLIENT_ACCESS_KEY;
const bypass = process.env.REACT_APP_BYPASS_LOGIN;
const URLS = require("../../../urls.js");

const StatusCodes = require('../../../status-codes/status-codes.js')
const ErrorCodes = require('../../../error-codes/error-codes.js')

const ReportInfoModule = ({user, reportId, isSmallScreen, showStatus = true, readAsMedic = false}) => {
    const navigate = useNavigate();
    const [reportData, setReportData] = useState(null);
    const [reportTypes, setReportTypes] = useState(null);
    const [medics, setMedics] = useState(null);
    const [readStatus, setReadStatus] = useState(null);
    const [finishedStatus, setFinishedStatus] = useState(null);
    const [medicReadStatus, setMedicReadStatus] = useState(null);
    const [reportStatus, setReportStatus] = useState(null); // workflow status from reports.status
    const [selectedStatusValue, setSelectedStatusValue] = useState(""); // dropdown selection

    const REPORT_STATUS_OPTIONS = [
        { value: "", label: "Not assigned" },
        { value: "Medical Director Review", label: "Medical Director Review" },
        { value: "Followup", label: "Followup" },
        { value: "Checked By Medical Director", label: "Checked By Medical Director" }
    ];

    const handleReadButtonClick = () => {
        console.log('readStatus changed');
        if (readStatus) {
            setReadStatus(0);
        } else {
            setReadStatus(1);
        }
    };

    const handleFinishedButtonClick = () => {
        console.log('finishedStatus changed');
        if (finishedStatus) {
            setFinishedStatus(0);
        } else {
            setFinishedStatus(1);
        }
    };

    const handleMedicReadButtonClick = () => {
        console.log('medicReadStatus changed');
        if (medicReadStatus) {
            setMedicReadStatus(0);
        } else {
            setMedicReadStatus(1);
        }
    };

    const handleReprocessClick = async () => {
        // Prompt user for reprocessing type
        const reprocessType = window.confirm(
            'Choose reprocessing type:\n\n' +
            'Click OK for FULL REPROCESSING (re-runs all tests and analysis)\n' +
            'Click Cancel for UPDATE RESULTS ONLY (updates test results for existing report)'
        );

        const url = URLS.REPROCESS_REPORT;
        const dict = {
            report_id: reportId,
            reprocess_type: reprocessType ? 'full' : 'update_results',
            CLIENT_ACCESS_KEY: process.env.REACT_APP_CLIENT_ACCESS_KEY
        };

        let responseData;
        
        await Network.post(url, dict, (response) => {
            responseData = response.data;
            const extractedErrorCode = responseData.toString();
            const status = response.status

            if (Network.checkValidStatusAndResponse(response)) {
                const message = reprocessType 
                    ? 'Report full reprocessing started successfully - return to home page to await completion'
                    : 'Report results updated successfully';
                alert(message);
                
                if (reprocessType) {
                    // Navigate back to home page after full reprocessing
                    navigate('/home');
                }
            } else {
                alert('Error reprocessing report');
            }
        });
    };

    const handleStoreAsDemoClick = async () => {
        const url = URLS.STORE_DEMO_REPORT;
        const dict = {
            report_id: reportId,
            CLIENT_ACCESS_KEY: process.env.REACT_APP_CLIENT_ACCESS_KEY
        };

        let responseData;
        
        await Network.post(url, dict, (response) => {
            responseData = response.data;
            const status = response.status;

            if (Network.checkValidStatusAndResponse(response)) {
                alert('Report stored as demo successfully');
            } else {
                alert('Error storing report as demo');
            }
        });
    };

    const handleProtocolCheck = async () => {
        const url = URLS.AGENT2_PROTOCOL_ANALYSIS;
        const dict = {
            report_id: reportId,
            CLIENT_ACCESS_KEY: process.env.REACT_APP_CLIENT_ACCESS_KEY
        };

        let responseData;
        
        try {
            await Network.post(url, dict, (response) => {
                responseData = response.data;
                const status = response.status;

                if (Network.checkValidStatusAndResponse(response)) {
                    const suggestionsAdded = responseData.suggestions_added || 0;
                    alert(`Agent2 Protocol Analysis completed successfully!\n\nAnalysis Summary:\n- Overall Compliance: ${responseData.analysis?.overall_compliance || 'Unknown'}\n- Total Findings: ${responseData.analysis?.statistics?.total_findings || 0}\n- Suggestions Added: ${suggestionsAdded}\n\nPlease refresh the page to see the new suggestions.`);
                    
                    // Optionally refresh the page to show new suggestions
                    // window.location.reload();
                } else {
                    alert('Error running Agent2 protocol analysis');
                }
            });
        } catch (error) {
            console.error('Error in protocol check:', error);
            alert('Error running Agent2 protocol analysis');
        }
    };

    const updateReadFinishedStatus = async (reportId, readStatus, finishedStatus, medicReadStatus) => {
        const url = URLS.UPDATE_REPORT_STATUS;

        let currentReadStatus = readStatus ? 'read' : 'unread';
        let currentFinishedStatus = finishedStatus ? 'finished' : 'unfinished';
        let currentMedicReadStatus = medicReadStatus ? 'medic_read' : 'medic_unread';

        const dict = {
            // uid: user.uid,
            report_id: reportId,
            read_status: currentReadStatus,
            finished_status: currentFinishedStatus,
            medic_read_status: currentMedicReadStatus,
            CLIENT_ACCESS_KEY: key
        };

        let responseData;

        await Network.post(url, dict, (response) => {
            responseData = response.data;
            const extractedErrorCode = responseData.toString();
            const status = response.status

            if (Network.checkValidStatusAndResponse(response)) {
                // do something
            } else if (status == StatusCodes.INTERNAL_SERVER_ERROR) {
                console.log("Server Error")
            } else {
                console.log("Unknown Error")
            }
        });

        return responseData;
    };

    const handleAssignStatusClick = async () => {
        const url = URLS.UPDATE_REPORT_STATUS_VALUE;
        const dict = {
            report_id: reportId,
            status: selectedStatusValue || null,
            CLIENT_ACCESS_KEY: key
        };

        await Network.post(url, dict, (response) => {
            if (Network.checkValidStatusAndResponse(response)) {
                setReportStatus(selectedStatusValue || null);
            } else {
                alert("Error updating report status");
            }
        });
    };

    // function that gets one reports from the server
    const getOneReport = async (reportId) => {
        const url = URLS.GET_ONE_REPORT;
        const dict = {
            // uid: user.uid,
            report_id: reportId,
            CLIENT_ACCESS_KEY: key
        };
        let responseData;

        await Network.post(url, dict, (response) => {
            responseData = response.data;
            const extractedErrorCode = responseData.toString();
            const status = response.status

            if (Network.checkValidStatusAndResponse(response)) {
                // do something

            } else if (status == StatusCodes.INTERNAL_SERVER_ERROR) {
                console.log("Server Error")

            } else {
                console.log("Unknown Error")
            }
        });
        return responseData;
    };

    useEffect(() => {
        getOneReport(reportId)
            .then((data) => {
                console.log('info data:', data[0]);
                setReportData(data);
                // Sort report types by tier (primary, secondary, other) and include auxiliary types
                const sortedTypes = data[0].report_type.split(',')
                    .sort((a, b) => {
                        const getTierIndex = (type) => {
                            if (primaryTypeTagNames.includes(type)) return 0;
                            if (secondaryTypeTagNames.includes(type)) return 1;
                            if (tertiaryTypeTagNames.includes(type)) return 2;
                            if (auxiliaryTypeTagNames.includes(type)) return 3;
                            if (otherTagNames.includes(type)) return 4;
                            return 4; // fallback for any unknown types
                        };
                        return getTierIndex(a) - getTierIndex(b);
                    });
                setReportTypes(sortedTypes);
                setMedics(JSON.parse(data[0].medics));
                setFinishedStatus(data[0].report_marked_finished);
                
                // Handle regular read status
                if (!readAsMedic) {
                    if (!data[0].report_marked_read) {
                        if (bypass !== 'true') {
                            setReadStatus(1);
                        } else {
                            setReadStatus(0);
                        }
                    } else {
                        setReadStatus(1);
                    }
                } else {
                    setReadStatus(data[0].report_marked_read);
                }
                
                // Handle medic read status
                if (readAsMedic) {
                    setMedicReadStatus(1);
                } else {
                    setMedicReadStatus(data[0].report_marked_medic_read);
                }
                setReportStatus(data[0].status ?? null);
                setSelectedStatusValue(data[0].status ?? "");
            })
            .catch((error) => {
                console.log('Error:', error);
            });
    }, []);

    useEffect(() => {
        console.log('changing statuses');
        if (readStatus !== null && finishedStatus !== null && medicReadStatus !== null) {
            console.log('Read Status:', readStatus);
            console.log('Finished Status:', finishedStatus);
            console.log('Medic Read Status:', medicReadStatus);
            updateReadFinishedStatus(reportId, readStatus, finishedStatus, medicReadStatus);
        }
    }, [readStatus, finishedStatus, medicReadStatus]);

    const createInfoListElement = (elementName, elementValue) => {
        return (
            elementValue !== null && elementValue !== 'null' ? ( // if elementValue is null, display 'insufficient data'
            <DualColListElement
            elementName={elementName}
            elementValue={elementValue}   
            />
            ) : (
            <DualColListElement
            elementName={elementName}
            elementValue='Insufficient Data'
            />
            )
        );
    };

    const convertToLocaleString = (dateString) => {
        const options = { 
            hour: 'numeric', 
            minute: 'numeric', 
            hour12: true, 
            year: 'numeric', 
            month: 'numeric', 
            day: 'numeric'
        };
    
        // if datestring is undefined or null, return 'no data'
        if (dateString === undefined || dateString === null) {
            return 'no data';
        }

        // Print out unconverted date
        // console.log('original dateString:', dateString);
    
        // convert the date string to a valid ISO 8601 date string and add Z to the end to indicate UTC time
        dateString = dateString.replace(' ', 'T');
        dateString = dateString + 'Z';

        // now convert the date string (which represnets a date in GMT) to a date object
        const date = new Date(dateString);
        
        // Print new date
        // console.log('new date:', date); // this is printing out GMT as CDT
    
        return date.toLocaleString([], options);
    };

    return (
        <div className={isSmallScreen ? "standard-module-screen-small" : "standard-module"}>
            {/* <div>
                <h2 className='module-title'>Report Information</h2>
            </div> */}
            {reportData && reportTypes && medics ? (
                <div>
                    {showStatus && (
                        <div style={{display: 'flex', flexDirection: 'row', gap: '10px'}}>
                            <div style={{display: 'flex', gap: '5px'}}>
                                <button 
                                    className="enter-button" 
                                    style={{
                                        marginBottom: '10px',
                                        backgroundColor: readStatus ? 'var(--green)' : 'var(--red)',
                                        color: 'white'
                                    }} 
                                    onClick={() => handleReadButtonClick(reportId, readStatus)}
                                >
                                    <i className={`fas ${readStatus ? 'fa-eye' : 'fa-eye-slash'}`}></i>
                                    <span style={{marginLeft: '5px'}}>{readStatus ? 'Report is Read' : 'Report is Unread'}</span>
                                </button>
                            </div>
                            <div style={{display: 'flex', gap: '5px'}}>
                                <button 
                                    className="enter-button" 
                                    style={{
                                        marginBottom: '10px',
                                        backgroundColor: finishedStatus ? 'var(--green)' : 'var(--red)',
                                        color: 'white'
                                    }}
                                    onClick={() => handleFinishedButtonClick(reportId, finishedStatus)}
                                >
                                    <i className={`fas ${finishedStatus ? 'fa-check' : 'fa-times'}`}></i>
                                    <span style={{marginLeft: '5px'}}>{finishedStatus ? 'Report is Finished' : 'Report is Unfinished'}</span>
                                </button>
                            </div>
                            <div style={{display: 'flex', gap: '5px'}}>
                                <button 
                                    className="enter-button" 
                                    style={{
                                        marginBottom: '10px',
                                        backgroundColor: medicReadStatus ? 'var(--green)' : 'var(--red)',
                                        color: 'white',
                                        opacity: 0.7,
                                        pointerEvents: 'none'
                                    }}
                                    // onClick={() => handleMedicReadButtonClick(reportId, medicReadStatus)}
                                >
                                    <i className={`fas ${medicReadStatus ? 'fa-user-md' : 'fa-user-md'}`}></i>
                                    <span style={{marginLeft: '5px'}}>{medicReadStatus ? 'Medic has accessed feedback' : 'Medic has not accessed feedback'}</span>
                                </button>
                            </div>
                            <div style={{display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap'}}>
                                <label htmlFor="report-status-select" style={{fontWeight: 600}}>Status:</label>
                                <select
                                    id="report-status-select"
                                    value={selectedStatusValue}
                                    onChange={(e) => setSelectedStatusValue(e.target.value)}
                                    style={{
                                        padding: '6px 10px',
                                        borderRadius: '5px',
                                        border: '2px solid var(--dark-grey)',
                                        fontSize: '0.9rem',
                                        minWidth: '200px'
                                    }}
                                >
                                    {REPORT_STATUS_OPTIONS.map((opt) => (
                                        <option key={opt.value || 'none'} value={opt.value}>{opt.label}</option>
                                    ))}
                                </select>
                                <button
                                    className="enter-button"
                                    style={{
                                        marginBottom: '10px',
                                        backgroundColor: (selectedStatusValue || '') === (reportStatus ?? '') ? 'var(--grey)' : 'var(--blue)',
                                        color: (selectedStatusValue || '') === (reportStatus ?? '') ? 'var(--dark-grey)' : 'white'
                                    }}
                                    onClick={handleAssignStatusClick}
                                >
                                    Assign Status
                                </button>
                            </div>
                            {user?.subscriptions?.includes('ADMIN_SUB') && (
                                <>
                                    <div style={{display: 'flex', gap: '5px'}}>
                                        <button 
                                            className="enter-button" 
                                            style={{marginBottom: '10px'}} 
                                            onClick={handleReprocessClick}
                                        >
                                            <i className="fas fa-sync"></i>
                                        </button>
                                    </div>
                                    <div style={{display: 'flex', gap: '5px'}}>
                                        <button 
                                            className="enter-button" 
                                            style={{marginBottom: '10px'}} 
                                            onClick={handleProtocolCheck}
                                            title="Run Agent2 Protocol Analysis - Analyze this report for protocol compliance using AI"
                                        >
                                            <i className="fas fa-clipboard-check"></i>
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    )}
                    {createInfoListElement('Incident #:', reportData[0].incident_number)}
                    {createInfoListElement('Response Began:', convertToLocaleString(reportData[0].call_begin))}
                    {createInfoListElement('Response Ended, Back in Service:', convertToLocaleString(reportData[0].unit_back_in_service))}
                    {createInfoListElement('PCR Completed:', convertToLocaleString(reportData[0].creation_timestamp))}
                    
                    <div className='list-element'>
                        <div className='small-div'>
                            <strong>Involved Medics:</strong>
                        </div>
                        <div className='small-div'>
                            {medics.map((medic) => (
                                <MedicTag medicNumber={medic.medic_name || medic.crew_member_id} isAuthor={medic.author} isButton={false}/>
                            ))}
                        </div>
                    </div>

                    <div className='list-element'>
                        <div className='small-div'>
                            <strong>Report Type(s):</strong>
                        </div>
                        <div className='small-div'>
                            {reportTypes.map((type) => (
                                <TypeTag reportType={type} isButton={false}/>
                            ))}
                        </div>
                    </div>

                    <div className='list-element'>
                        <div className='small-div'>
                            <strong>Summary:</strong>
                        </div>
                        <div className='small-div' style={{textAlign: 'left'}}>
                            {reportData[0].narrative_summarized}
                        </div>
                    </div>
                    <div style={{marginTop: '20px'}}>
                        {reportData && reportData[0] && reportData[0].report_object ? (() => {
                            try {
                                const raw = reportData[0].report_object;
                                const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
                                return <ReportTimeline reportData={parsed} />;
                            } catch (err) {
                                return <div style={{ color: 'red', fontWeight: 'bold' }}>Could not load timeline due to parsing error.</div>;
                            }
                        })() : (
                            <div>Loading timeline...</div>
                        )}
                    </div>
                </div>
            ) : (
                <div>If this box does not populate, your report has beed reprocessed. Look for a new feedback email.</div>
            )}
        </div>
    );
}

export default ReportInfoModule;